import { CSSProperties, useEffect, useMemo, useState } from "react";

import { Classes, Colors } from "@blueprintjs/core";
import styled from "@emotion/styled";
import {
  ArrowLeftRight,
  Loader2,
  MoreHorizontal,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { Header } from "@/components/pieces/table/Header";
import { Avatar } from "@/components/pieces/users/Avatar";
// Remove Blueprint overrides and use shadcn/ui
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BP_NS,
  INNER_BORDER_COLOR,
  LIGHTEST_BACKGROUND,
  STANDARD_NAV_HEIGHT,
} from "@/css/constants";
import { WorkspaceMemberRole } from "@/graphql";
import { HeaderConfig } from "@/types/tables";
import {
  MemberTableField,
  OnCheckHandler,
  StatsMember,
} from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { parseDecimal } from "@/utils/string";

import { CreditsWithAccountsPopover } from "./CreditsWithAccounts";
import { GroupsWithPopover } from "./GroupsWithPopover";

const TABLE_MIN_WIDTH = 900;

interface MemberHeaderConfig extends HeaderConfig {
  field: MemberTableField | "email" | "checkbox" | "actions" | "groups";
}

const DEFAULT_HEADERS: MemberHeaderConfig[] = [
  {
    field: "checkbox",
    title: "",
    type: "checkbox",
    widthClass: "w-12 xl:w-8", // Fixed width for checkbox column
  },
  {
    field: "name",
    title: "Name",
    widthClass: "w-60", // Fixed width for name column
  },
  {
    field: "email",
    title: "Email",
    disableSorting: true,
    widthClass: "w-52", // Fixed width for email column
  },
  {
    field: "groups",
    title: "Groups",
    disableSorting: true,
    widthClass: "w-36", // Fixed width for groups column
  },
  {
    field: "balance",
    title: "Available Credits",
    widthClass: "w-24", // Fixed width for balance column
  },
];

const ADMIN_HEADERS = DEFAULT_HEADERS.concat([
  {
    field: "actions",
    title: "Actions",
    type: "text",
    disableSorting: true,
    justifyContent: "center",
    widthClass: "w-16", // Fixed width for actions column
  },
]);

export const isAgentMember = ({ role }: { role: string }) => {
  return role && ["ADMIN", "STAFF"].includes(role);
};

export const isAdminMember = ({ role }: { role: WorkspaceMemberRole }) =>
  role === WorkspaceMemberRole.Admin;

export function MemberName({
  row,
  style = {},
}: {
  row: StatsMember;
  style?: CSSProperties;
}) {
  const { user } = row;
  const label = user.name || user.email;
  const postfix = isAdminMember(row) ? (
    <Badge className="text-sm text-white bg-primary">Admin</Badge>
  ) : isAgentMember(row) ? (
    <Badge className="text-sm text-white bg-primary">Staff</Badge>
  ) : null;

  return (
    <span>
      <span className="mr-2" style={style}>
        {" "}
        {label}
      </span>
      {postfix}
    </span>
  );
}

interface WorkspaceMemberTableProps {
  loading?: boolean;
  data: StatsMember[];
  currentSort: { field?: MemberTableField; asc?: boolean };
  onSort: (field: MemberTableField) => void;
  onTransferCredits: (user: StatsMember) => void;
  onUpdateRole: (user: StatsMember, newRole: WorkspaceMemberRole) => void;
  onRemove: (user: StatsMember) => void;
  checkedData?: string[] | number[];
  selectAllUsers?: boolean;
  excludedUsers?: string[];
  onSelectedMember: (member: StatsMember) => void;
  onSetGroup: (member: StatsMember) => void;
  onCheck: OnCheckHandler;
}

export function WorkspaceMemberTable({
  loading = false,
  data,
  currentSort,
  onSort,
  onTransferCredits,
  onUpdateRole,
  onRemove,
  checkedData,
  selectAllUsers,
  excludedUsers = [],
  onCheck,
  onSelectedMember,
  onSetGroup,
}: WorkspaceMemberTableProps) {
  const headers = isWorkspaceAdmin() ? ADMIN_HEADERS : DEFAULT_HEADERS;

  // Keep track of displayed data to prevent flash during loading
  const [displayedData, setDisplayedData] = useState<StatsMember[]>(data);

  // Update displayed data only when new data arrives and we're not loading
  useEffect(() => {
    if (!loading && data.length >= 0) {
      setDisplayedData(data);
    }
  }, [data, loading]);

  // Initialize displayed data if it's empty but we have data
  useEffect(() => {
    if (displayedData.length === 0 && data.length > 0) {
      setDisplayedData(data);
    }
  }, [data, displayedData.length]);

  const selectedsSet = useMemo(
    () => new Set<string | number>(checkedData),
    [checkedData],
  );

  const excludedSet = useMemo(
    () => new Set<string | number>(excludedUsers),
    [excludedUsers],
  );

  const checkedAll =
    selectAllUsers ||
    (displayedData.length > 0 &&
      displayedData.every((row) => selectedsSet.has(row.id)));

  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);

  const isRowChecked = (memberId: string | number) => {
    if (selectAllUsers) {
      return !excludedSet.has(memberId);
    }
    return selectedsSet.has(memberId);
  };

  return (
    <TOCBox width="100%" height="100%">
      <TOC className={Classes.HTML_TABLE}>
        <thead>
          <tr>
            {headers.map((config, index) => (
              <Header
                key={index}
                config={config}
                currentSort={currentSort}
                onSort={onSort}
                checkedAll={checkedAll}
                onCheckAll={(checked) => {
                  onCheck({
                    checked: checked,
                    isCheckAll: true,
                  });
                }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedData.map((member, n) => {
            const { user } = member;

            return (
              <tr
                key={`workspace-item-${member.id}-${n}`}
                className={loading ? "opacity-75" : ""}
              >
                <td className="text-left w-12 whitespace-nowrap">
                  <Checkbox
                    checked={isRowChecked(member.id)}
                    onCheckedChange={(checked) => {
                      onCheck({
                        member: member,
                        checked: checked as boolean,
                        isCheckAll: false,
                      });
                    }}
                  />
                </td>
                <td className="w-48 overflow-hidden text-ellipsis whitespace-nowrap">
                  <div className="flex items-center">
                    <Tooltip>
                      <TooltipTrigger>
                        <Avatar size={24} user={user} />
                      </TooltipTrigger>
                      <TooltipContent>{user.email}</TooltipContent>
                    </Tooltip>
                    <div className="ml-[5px] truncate">
                      <a
                        onClick={() => {
                          onSelectedMember(member);
                          return;
                        }}
                      >
                        <MemberName row={member} />
                      </a>
                    </div>
                  </div>
                </td>
                <td className="w-64 overflow-hidden text-ellipsis whitespace-nowrap">
                  <span className="mr-[5px]"> {user?.email}</span>
                </td>
                <td className="w-36 overflow-hidden text-ellipsis whitespace-nowrap">
                  <GroupsWithPopover groups={member.groups} />
                </td>
                <td className="w-44 overflow-hidden text-center text-ellipsis whitespace-nowrap">
                  <CreditsWithAccountsPopover
                    totalBalance={parseDecimal(member.totalBalance)}
                    ledgerAccounts={ensureNonNullArray(member.ledgerAccounts)}
                  />
                </td>
                {isWorkspaceAdmin() && (
                  <td className="w-32 text-center overflow-hidden">
                    <DropdownMenu
                      open={openMenuId === member.id}
                      onOpenChange={(open) =>
                        setOpenMenuId(open ? member.id : null)
                      }
                    >
                      <DropdownMenuTrigger asChild>
                        <Button intent="secondary" size="sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            onTransferCredits(member);
                            setOpenMenuId(null);
                          }}
                        >
                          <ArrowLeftRight className="mr-2 h-4 w-4" />
                          Transfer Credits
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            onSetGroup(member);
                            setOpenMenuId(null);
                          }}
                        >
                          <Users className="mr-2 h-4 w-4" />
                          Set Group
                        </DropdownMenuItem>
                        {isAdminMember(member) ? (
                          <DropdownMenuItem
                            onClick={() => {
                              onUpdateRole(member, WorkspaceMemberRole.Member);
                              setOpenMenuId(null);
                            }}
                          >
                            <UserPlus className="mr-2 h-4 w-4" />
                            Convert to Member
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => {
                              onUpdateRole(member, WorkspaceMemberRole.Admin);
                              setOpenMenuId(null);
                            }}
                          >
                            <UserPlus className="mr-2 h-4 w-4" />
                            Convert to Admin
                          </DropdownMenuItem>
                        )}
                        {!isAdminMember(member) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                onRemove(member);
                                setOpenMenuId(null);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove User
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </TOC>
      {!displayedData.length ? (
        <div className="flex w-full bg-white border-b border-gray-300 min-h-[200px] py-6 justify-center items-center">
          <div>{!loading ? "No Clients!" : ""}</div>
        </div>
      ) : null}
      {loading ? (
        <div className="flex justify-center p-2 absolute top-20 left-0 right-0 opacity-50 w-full h-full bg-white">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : null}
    </TOCBox>
  );
}

export const TOCBox = styled(Box)`
  position: relative;

  @media (max-width: ${TABLE_MIN_WIDTH + 600}px) {
    overflow-x: auto;
  }
`;

export const TOC = styled.table`
  &.${BP_NS}-html-table {
    width: 100%;
    font-weight: 400;
    font-size: 15px;
    line-height: 20px;
    min-width: ${TABLE_MIN_WIDTH}px;
    position: relative;
    table-layout: fixed; /* Enforce fixed column widths */

    .right-align {
      text-align: right;
    }

    &.noboder-table {
      & tbody {
        td {
          border: none;
        }
        & tr:nth-of-type(even) {
          background-color: ${Colors.WHITE};
        }

        & tr:hover {
          background-color: ${LIGHTEST_BACKGROUND};
          cursor: pointer;
        }
      }
      & tr th {
        border: none;
      }
    }

    & tbody {
      & tr:nth-of-type(odd) {
        background-color: ${Colors.WHITE};
      }

      & tr:nth-of-type(even) {
        background-color: ${LIGHTEST_BACKGROUND};
      }

      & tr {
        height: 60px;
        padding: 16px;
        border-style: solid;
        border-color: ${Colors.LIGHT_GRAY5};
        border-width: 0 0 1px 0;
        &:first-of-type {
          border-top: none;
        }
      }

      & td {
        border-style: solid;
        border-color: ${INNER_BORDER_COLOR};
        border-width: 0 1px 0 0;
        &:first-of-type {
          padding-left: 24px;
        }
        &:last-of-type {
          padding-right: 24px;
        }
      }
    }

    & thead {
      & tr {
        & th:first-of-type:before {
          display: none;
        }
        & th {
          border-style: solid;
          border-color: ${INNER_BORDER_COLOR};
          border-width: 0 1px 0 0;
          @media (min-width: ${TABLE_MIN_WIDTH + 500}px) {
            position: sticky;
            /* set top for sticky work */
            top: ${STANDARD_NAV_HEIGHT}px;
            z-index: 9;
          }

          /* Sticky just work with bow shadow not border */
          box-shadow: inset 0 -1px 0 ${Colors.LIGHT_GRAY2};

          /* and one small fix for weird FF behavior, described in https://stackoverflow.com/questions/7517127/ */
          background-clip: padding-box;

          &.header {
            background-color: ${Colors.WHITE};
            font-size: 15px;
            font-weight: 400;
            color: ${Colors.GRAY3};
            &.active-sort {
              font-weight: 600;
              color: ${Colors.DARK_GRAY1};
            }
          }
          &:first-of-type {
            padding-left: 24px;
          }
          &:last-of-type {
            padding-right: 24px;
          }
        }
      }
    }
    &.${Classes.HTML_TABLE} td,
    &.${Classes.HTML_TABLE} th {
      padding: 10px 12px;
      vertical-align: middle;
    }
    &.${Classes.HTML_TABLE} tr:first-of-type td {
      box-shadow: none;
    }
  }
`;
